import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FolderTree, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { DOCUMENTS_QUERY } from '../graphql/queries';
import { Drawer } from './Drawer';
import { DocTreePanel } from './DocTreePanel';
import { useDocTreePinned, useDocTreeState } from '../hooks/useDocTreeState';
import { buildDocTree, type DocTreeRoot } from '../lib/docTree';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';

interface DocumentsData {
  documents: { path: string; type: string | null; treeLabel: string; spaceId: string | null }[];
}

/**
 * The document tree, from the already-fetched index. Error-ignoring on
 * purpose: the cache is usually warm, and a failed fetch just means no tree —
 * never a degraded documents page.
 */
function useDocTree(activePath?: string | null) {
  const { data } = useQuery<DocumentsData>(DOCUMENTS_QUERY, { errorPolicy: 'ignore' });
  const roots: DocTreeRoot[] = useMemo(
    () => buildDocTree(data?.documents ?? []),
    [data?.documents]
  );
  const { expandedIds, toggleFolder } = useDocTreeState(roots, activePath);
  return { roots, expandedIds, toggleFolder };
}

interface DocTreeNavProps {
  /** The open document's full path (active highlight + auto-reveal). */
  activePath?: string | null;
  /** Extra classes on the toggle button (e.g. `xl:hidden` beside a docked pane). */
  buttonClassName?: string;
  /** Reports drawer open/close so the page can pause gestures underneath. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * The file-tree DRAWER and its toggle, for the documents area. This is a
 * documents-scoped surface: the burger SideNav stays app-level navigation with
 * its single "Documents" link (the two-tier IA in lib/nav.ts), while this
 * drawer navigates WITHIN the document tree. The two never stack: this trigger
 * sits in page content, under the burger drawer's own overlay.
 */
export function DocTreeNav({ activePath, buttonClassName = '', onOpenChange }: DocTreeNavProps) {
  const [open, setOpen] = useState(false);
  const { roots, expandedIds, toggleFolder } = useDocTree(activePath);

  if (roots.length === 0) return null;

  const setOpenAndReport = (value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpenAndReport(true)}
        aria-label="Browse documents"
        className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES} ${buttonClassName}`}
      >
        <FolderTree className="w-5 h-5" aria-hidden />
      </button>

      <Drawer
        isOpen={open}
        onClose={() => setOpenAndReport(false)}
        ariaLabel="Document tree"
        closeLabel="Close document tree"
        overlayTestId="doctree-overlay"
        headerContent={
          <span className="text-lg font-semibold text-[var(--text-primary)]">Documents</span>
        }
      >
        <div className="flex-1 p-4 overflow-y-auto overscroll-contain">
          <DocTreePanel
            roots={roots}
            expandedIds={expandedIds}
            onToggleFolder={toggleFolder}
            activePath={activePath}
            onNavigate={() => setOpenAndReport(false)}
          />
        </div>
      </Drawer>
    </>
  );
}

/**
 * The DOCKED tree pane for wide desktop (`xl:` and up), where there is room
 * beside the document column (below `xl` the overlay drawer takes over). Also
 * collapsible to a slim rail; the choice persists.
 */
export function DocTreeAside({ activePath }: { activePath?: string | null }) {
  const { roots, expandedIds, toggleFolder } = useDocTree(activePath);
  const { pinned, setPinned } = useDocTreePinned();

  if (roots.length === 0) return null;

  if (!pinned) {
    return (
      <aside className="hidden xl:block shrink-0 sticky top-20">
        <button
          type="button"
          onClick={() => setPinned(true)}
          aria-label="Show document tree"
          className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
        >
          <PanelLeftOpen className="w-5 h-5" aria-hidden />
        </button>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Document tree"
      className="hidden xl:block w-72 shrink-0 sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain pt-4 pr-2"
    >
      <div className="flex items-center justify-between px-3 pb-2">
        <span className="text-sm font-semibold text-[var(--text-primary)]">Documents</span>
        <button
          type="button"
          onClick={() => setPinned(false)}
          aria-label="Hide document tree"
          className={`inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
        >
          <PanelLeftClose className="w-4 h-4" aria-hidden />
        </button>
      </div>
      <DocTreePanel
        roots={roots}
        expandedIds={expandedIds}
        onToggleFolder={toggleFolder}
        activePath={activePath}
      />
    </aside>
  );
}
